import { escapeHTML, registerServiceWorker, placeholderImage, trackMediaLoad, track } from './common.js';

const root = document.querySelector('#journal-root');

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(value))
  : 'Unscheduled';

async function api(path) {
  const response = await fetch(path);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  return data;
}

function articleSlug() {
  const querySlug = new URLSearchParams(location.search).get('post');
  if (querySlug) return querySlug;
  const match = location.pathname.match(/^\/journal\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : '';
}

function cardTemplate(post, index) {
  const tags = post.tags.map((tag) => `<span>#${escapeHTML(tag)}</span>`).join('');
  const articleUrl = `/journal/${encodeURIComponent(post.slug)}`;
  return `
    <article class="journal-card" style="--card-index:${index}">
      <a class="journal-card__cover" href="${articleUrl}" aria-label="Read ${escapeHTML(post.title)}">
        ${post.cover
          ? `<img src="${escapeHTML(post.cover.url)}" alt="${escapeHTML(post.cover.alt || post.title)}" loading="${index < 2 ? 'eager' : 'lazy'}" decoding="async" />`
          : `<img src="${placeholderImage()}" alt="" width="1200" height="800" />`}
        <span>${String(index + 1).padStart(2, '0')} / FIELD NOTE</span>
      </a>
      <div class="journal-card__body">
        <div class="journal-card__meta"><time datetime="${escapeHTML(post.publishedAt || post.updatedAt)}">${escapeHTML(formatDate(post.publishedAt || post.updatedAt))}</time><span>${post.readingMinutes} MIN READ</span></div>
        <h2><a href="${articleUrl}">${escapeHTML(post.title)}</a></h2>
        <p>${escapeHTML(post.excerpt)}</p>
        <div class="journal-card__foot"><div class="journal-card__tags">${tags}</div><a class="journal-card__arrow" href="${articleUrl}" aria-label="Read ${escapeHTML(post.title)}">↗</a></div>
      </div>
    </article>
  `;
}

async function renderIndex() {
  const { posts } = await api('/api/blog');
  const tags = [...new Set(posts.flatMap((post) => post.tags))].sort((a, b) => a.localeCompare(b));
  root.innerHTML = `
    <section class="journal-hero">
      <div><p class="journal-kicker">NERO / JOURNAL / ${String(posts.length).padStart(2, '0')} ENTRIES</p><h1>Field notes.<em>Nothing polished away.</em></h1></div>
      <p class="journal-hero__note">New work, half-formed ideas, production diaries and the things worth remembering between releases.</p>
    </section>
    <section class="journal-tools" aria-label="Journal controls">
      <label class="journal-search"><span>⌕</span><input type="search" data-journal-search placeholder="Search titles, stories or tags…" autocomplete="off" /></label>
      <select data-journal-sort aria-label="Sort journal entries"><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="title">A–Z</option></select>
      <a class="journal-rss" href="/rss.xml">RSS FEED ↗</a>
    </section>
    <div class="journal-tags" data-journal-tags>
      <button class="is-active" type="button" data-tag="">All notes</button>
      ${tags.map((tag) => `<button type="button" data-tag="${escapeHTML(tag)}">${escapeHTML(tag)}</button>`).join('')}
    </div>
    <section class="journal-grid" data-journal-grid></section>
    <section class="journal-empty" data-journal-empty hidden><span>NO NOTES MATCH THAT SIGNAL</span><small>Try another phrase or clear the active tag.</small></section>
  `;
  const grid = root.querySelector('[data-journal-grid]');
  const watchCovers = () => grid.querySelectorAll('img').forEach(trackMediaLoad);
  const empty = root.querySelector('[data-journal-empty]');
  const search = root.querySelector('[data-journal-search]');
  const sort = root.querySelector('[data-journal-sort]');
  let activeTag = '';
  const draw = () => {
    const query = search.value.trim().toLowerCase();
    const visible = posts
      .filter((post) => {
        const haystack = `${post.title} ${post.excerpt} ${post.tags.join(' ')}`.toLowerCase();
        return (!query || haystack.includes(query)) && (!activeTag || post.tags.includes(activeTag));
      })
      .sort((a, b) => sort.value === 'oldest'
        ? new Date(a.publishedAt) - new Date(b.publishedAt)
        : sort.value === 'title'
          ? a.title.localeCompare(b.title)
          : new Date(b.publishedAt) - new Date(a.publishedAt));
    grid.innerHTML = visible.map(cardTemplate).join('');
    watchCovers();
    empty.hidden = visible.length > 0;
  };
  search.addEventListener('input', draw);
  sort.addEventListener('change', draw);
  root.querySelector('[data-journal-tags]').addEventListener('click', (event) => {
    const button = event.target.closest('[data-tag]');
    if (!button) return;
    activeTag = button.dataset.tag;
    root.querySelectorAll('[data-tag]').forEach((item) => item.classList.toggle('is-active', item === button));
    draw();
  });
  draw();
}

async function renderArticle(slug) {
  const { post } = await api(`/api/blog/${encodeURIComponent(slug)}`);
  document.title = `${post.seoTitle || post.title} — NERO / Journal`;
  const description = document.querySelector('meta[name="description"]');
  if (description) description.content = post.seoDescription || post.excerpt;
  root.innerHTML = `
    <article class="article">
      <header class="article__header">
        <div><p class="journal-kicker">NERO / FIELD NOTE / ${escapeHTML(post.tags[0] || 'JOURNAL')}</p><h1>${escapeHTML(post.title)}</h1></div>
        <div class="article__meta">
          <span>Published<br /><strong>${escapeHTML(formatDate(post.publishedAt || post.updatedAt))}</strong></span>
          <span>Reading time<br /><strong>${post.readingMinutes} minutes</strong></span>
          <span>${post.tags.map((tag) => `#${escapeHTML(tag)}`).join(' · ') || 'UNTAGGED NOTE'}</span>
          <button type="button" data-share-post>Copy article link ↗</button>
        </div>
      </header>
      ${post.cover ? `<figure class="article__cover" data-media-frame><img src="${escapeHTML(post.cover.url)}" alt="${escapeHTML(post.cover.alt || post.title)}" decoding="async" /></figure>` : ''}
      <div class="article__layout">
        <aside class="article__aside"><span>ENTRY / ${escapeHTML(post.slug)}</span><a href="/journal">← All field notes</a></aside>
        <div class="article__content">${post.contentHtml || `<p>${escapeHTML(post.excerpt)}</p>`}</div>
        <aside class="article__aside">NERO / JOURNAL<br />PORTUGAL → ANYWHERE</aside>
      </div>
    </article>
  `;
  root.querySelectorAll('img').forEach(trackMediaLoad);
  root.querySelector('[data-share-post]').addEventListener('click', async (event) => {
    try {
      await navigator.clipboard.writeText(location.href);
      event.currentTarget.textContent = 'Link copied ✓';
    } catch {
      event.currentTarget.textContent = location.href;
    }
  });
  const updateProgress = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    document.documentElement.style.setProperty('--reading-progress', `${max > 0 ? Math.min(100, scrollY / max * 100) : 0}%`);
  };
  addEventListener('scroll', updateProgress, { passive: true });
  updateProgress();
}

try {
  const slug = articleSlug();
  if (slug) await renderArticle(slug);
  else await renderIndex();
} catch (error) {
  root.innerHTML = `<section class="journal-empty journal-error"><span>TRANSMISSION INTERRUPTED</span><small>${escapeHTML(error.message)}</small><a href="/journal">Return to the journal ↗</a></section>`;
}

registerServiceWorker();
track('journal_page_view');
